import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLang } from "../i18n";
import { colors, fontHeading } from "../theme";
import { SITE } from "../siteConfig";
import CarCard from "../components/CarCard";
import ContactForm from "../components/ContactForm";
import CarFilters from "../components/CarFilters";
import { webpSmall } from "../imageUrls";

export default function Home({ cars, loading }) {
  const { t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const [prefillCar, setPrefillCar] = useState("");
  const [filters, setFilters] = useState({ brand: "", bodyType: "", fuel: "", transmission: "" });

  const filteredCars = useMemo(
    () =>
      cars.filter(
        (c) =>
          (!filters.brand || c.brand === filters.brand) &&
          (!filters.bodyType || c.bodyType === filters.bodyType) &&
          (!filters.fuel || c.fuel === filters.fuel) &&
          (!filters.transmission || c.transmission === filters.transmission)
      ),
    [cars, filters]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const car = params.get("coche") || params.get("car");
    if (car) {
      setPrefillCar(car);
      setTimeout(() => {
        document.getElementById("contacto")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
      navigate(location.pathname, { replace: true });
    } else if (location.hash) {
      setTimeout(() => {
        document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aboutImg = cars[1]?.images?.[0] || cars[0]?.images?.[0];

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div>
      {/* HERO */}
      <section style={s.hero}>
        <div style={s.heroOverlay} />
        <div style={s.heroContent} className="hero-pad fade-in">
          <p style={s.eyebrow}>{t.heroEyebrow}</p>
          <h1 style={s.heroTitle}>
            {t.heroTitle.split("\n").map((line, i) => (
              <span key={i}>
                {line}
                {i === 0 && <br />}
              </span>
            ))}
          </h1>
          <p style={s.heroSub}>{t.heroSub}</p>
          <div style={s.heroCta}>
            <button style={s.btnPrimary} className="btn-hover" onClick={() => scrollTo("coches")}>
              {t.heroCtaCars}
            </button>
            <button style={s.btnGhost} className="btn-hover" onClick={() => scrollTo("contacto")}>
              {t.heroCtaContact}
            </button>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section style={s.stats} className="stats-row">
        {[
          [String(cars.length || "7"), t.statCars],
          ["4", t.statAreas],
          ["100%", t.statDirect],
          ["24h", t.statWhatsapp],
        ].map(([n, l]) => (
          <div key={l} style={s.statItem}>
            <span style={s.statNum}>{n}</span>
            <span style={s.statLabel}>{l}</span>
          </div>
        ))}
      </section>

      {/* CARS */}
      <section id="coches" style={s.section} className="section-pad">
        <div style={s.sectionHeader}>
          <p style={s.eyebrowCenter}>{t.carsEyebrow}</p>
          <h2 style={s.sectionTitle}>{t.carsTitle}</h2>
          <div style={s.titleLine} />
          <p style={s.sectionSub}>{t.carsSub}</p>
        </div>
        {loading ? (
          <div style={s.loadingBox}>{t.loading}</div>
        ) : (
          <>
            <CarFilters cars={cars} filters={filters} setFilters={setFilters} />
            <p style={s.resultsCount}>
              {filteredCars.length === 1 ? t.filterResultsOne : t.filterResults(filteredCars.length)}
            </p>
            {filteredCars.length === 0 ? (
              <div style={s.loadingBox}>{t.filterNoResults}</div>
            ) : (
              <div style={s.grid} className="cars-grid">
                {filteredCars.map((car, i) => (
                  <CarCard key={car.id} car={car} index={i} />
                ))}
              </div>
            )}
          </>
        )}
      </section>

      {/* ABOUT */}
      <section id="nosotros" style={s.aboutSection} className="section-pad">
        <div style={s.aboutGrid} className="about-grid">
          <div style={s.aboutImgWrap}>
            {aboutImg && (
              <picture>
                <source type="image/webp" srcSet={webpSmall(aboutImg)} />
                <img
                  src={aboutImg}
                  width={1600}
                  height={1200}
                  loading="lazy"
                  decoding="async"
                  alt={SITE.name}
                  style={s.aboutImg}
                />
              </picture>
            )}
            <div style={s.aboutAccent} />
          </div>
          <div>
            <p style={s.eyebrow2}>{t.aboutEyebrow}</p>
            <h2 style={s.sectionTitle}>{t.aboutTitle}</h2>
            <div style={s.titleLineLeft} />
            <p style={s.aboutPara}>{t.aboutP1}</p>
            <p style={s.aboutPara}>{t.aboutP2}</p>
            <p style={s.aboutPara}>{t.aboutP3}</p>
            <div style={s.featuresList}>
              {t.aboutFeatures.map((f) => (
                <div key={f} style={s.featureItem}>
                  <span style={s.featureDot} />
                  {f}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contacto" style={s.section} className="section-pad">
        <div style={s.sectionHeader}>
          <p style={s.eyebrowCenter}>{t.contactEyebrow}</p>
          <h2 style={s.sectionTitle}>{t.contactTitle}</h2>
          <div style={s.titleLine} />
        </div>
        <div style={s.contactGrid} className="contact-grid">
          <div>
            <h3 style={s.contactInfoTitle}>{t.contactInfoTitle}</h3>
            <p style={s.contactInfoSub}>{t.contactInfoSub}</p>
            {[
              ["📍", t.labelAddress, SITE.address],
              ["📞", t.labelPhone, SITE.phone],
              ["🕐", t.labelHours, t.topBarHours],
            ].map(([icon, label, val]) => (
              <div key={label} style={s.contactRow}>
                <span style={s.contactIcon}>{icon}</span>
                <div>
                  <div style={s.contactLabel}>{label}</div>
                  <div style={s.contactVal}>{val}</div>
                </div>
              </div>
            ))}
            <div style={s.mapWrap}>
              <iframe
                title="map"
                src={SITE.mapEmbedSrc}
                width="100%"
                height="220"
                style={{ border: 0, display: "block" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
          <div style={s.formWrap}>
            <ContactForm cars={cars} prefillCar={prefillCar} />
          </div>
        </div>
      </section>
    </div>
  );
}

const s = {
  hero: {
    position: "relative",
    minHeight: "78vh",
    display: "flex",
    alignItems: "center",
    background: `linear-gradient(160deg, ${colors.bgAlt} 0%, #ffffff 55%, #fff 100%)`,
    overflow: "hidden",
    borderBottom: `1px solid ${colors.border}`,
  },
  heroOverlay: {
    position: "absolute",
    top: "-30%",
    right: "-10%",
    width: 700,
    height: 700,
    borderRadius: "50%",
    background: colors.redSoft,
    filter: "blur(10px)",
  },
  heroContent: { position: "relative", maxWidth: 1280, margin: "0 auto", padding: "60px 40px", width: "100%" },
  eyebrow: { fontSize: 12, letterSpacing: "0.18em", color: colors.red, marginBottom: 20, fontFamily: fontHeading, fontWeight: 700 },
  heroTitle: {
    fontSize: "clamp(38px, 6vw, 68px)",
    fontWeight: 800,
    lineHeight: 1.08,
    color: colors.ink,
    margin: "0 0 24px",
    fontFamily: fontHeading,
    letterSpacing: "-0.01em",
  },
  heroSub: { fontSize: 17, fontWeight: 400, color: colors.body, maxWidth: 520, lineHeight: 1.7, marginBottom: 40 },
  heroCta: { display: "flex", gap: 16, flexWrap: "wrap" },
  btnPrimary: {
    background: colors.red,
    color: "#fff",
    border: "none",
    padding: "16px 34px",
    fontSize: 12,
    letterSpacing: "0.08em",
    cursor: "pointer",
    fontFamily: fontHeading,
    fontWeight: 700,
    borderRadius: 6,
  },
  btnGhost: {
    background: "transparent",
    color: colors.ink,
    border: `1.5px solid ${colors.borderStrong}`,
    padding: "16px 34px",
    fontSize: 12,
    letterSpacing: "0.08em",
    cursor: "pointer",
    fontFamily: fontHeading,
    fontWeight: 700,
    borderRadius: 6,
  },
  stats: {
    background: colors.ink,
    display: "flex",
    justifyContent: "center",
  },
  statItem: {
    padding: "34px 44px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
    textAlign: "center",
    maxWidth: 220,
  },
  statNum: { fontSize: 30, fontWeight: 800, color: colors.red, lineHeight: 1, fontFamily: fontHeading },
  statLabel: { fontSize: 12, letterSpacing: "0.02em", color: "#cfcfcf" },
  section: { maxWidth: 1280, margin: "0 auto", padding: "90px 40px" },
  sectionHeader: { textAlign: "center", marginBottom: 56 },
  eyebrowCenter: { fontSize: 12, letterSpacing: "0.18em", color: colors.red, marginBottom: 14, fontFamily: fontHeading, fontWeight: 700 },
  eyebrow2: { fontSize: 12, letterSpacing: "0.18em", color: colors.red, marginBottom: 14, fontFamily: fontHeading, fontWeight: 700 },
  sectionTitle: { fontSize: "clamp(28px, 3.6vw, 42px)", fontWeight: 800, color: colors.ink, margin: "0 0 18px", fontFamily: fontHeading },
  titleLine: { width: 54, height: 3, background: colors.red, margin: "0 auto", borderRadius: 2 },
  titleLineLeft: { width: 54, height: 3, background: colors.red, margin: "0 0 24px", borderRadius: 2 },
  sectionSub: { color: colors.muted, fontSize: 14, marginTop: 16 },
  loadingBox: { textAlign: "center", padding: "60px 0", color: colors.muted, letterSpacing: "0.1em", fontSize: 13 },
  resultsCount: { textAlign: "center", color: colors.muted, fontSize: 13, marginTop: -14, marginBottom: 26 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 24 },
  aboutSection: { background: colors.bgAlt, padding: "90px 40px" },
  aboutGrid: { maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 70, alignItems: "center" },
  aboutImgWrap: { position: "relative" },
  aboutImg: { width: "100%", height: 460, objectFit: "cover", display: "block", borderRadius: 10 },
  aboutAccent: { position: "absolute", bottom: -18, left: -18, width: "55%", height: "55%", border: `3px solid ${colors.red}`, borderRadius: 10, zIndex: -1 },
  aboutPara: { fontSize: 15.5, lineHeight: 1.85, color: colors.body, marginBottom: 18, fontWeight: 400 },
  featuresList: { marginTop: 26, display: "flex", flexDirection: "column", gap: 13 },
  featureItem: { display: "flex", alignItems: "center", gap: 12, fontSize: 14.5, color: colors.ink, fontWeight: 500 },
  featureDot: { width: 7, height: 7, background: colors.red, borderRadius: "50%", flexShrink: 0 },
  contactGrid: { display: "grid", gridTemplateColumns: "1fr 1.15fr", gap: 70 },
  contactInfoTitle: { fontSize: 24, fontWeight: 800, color: colors.ink, margin: "0 0 14px", fontFamily: fontHeading },
  contactInfoSub: { fontSize: 14.5, color: colors.body, lineHeight: 1.8, marginBottom: 30 },
  contactRow: { display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 18 },
  contactIcon: { fontSize: 17, lineHeight: 1.5 },
  contactLabel: { fontSize: 11, color: colors.muted, letterSpacing: "0.06em", marginBottom: 2 },
  contactVal: { fontSize: 14.5, color: colors.ink, fontWeight: 500 },
  mapWrap: { marginTop: 22, borderRadius: 10, overflow: "hidden", border: `1px solid ${colors.border}` },
  formWrap: { background: "#fff", padding: "40px", border: `1px solid ${colors.border}`, borderRadius: 12, boxShadow: "0 10px 40px rgba(0,0,0,0.05)" },
};
